/**
 * AdminVoucherTemplatesPage.jsx — /admin/vouchers — vendor template CRUD.
 *
 * Lets an admin (or vendor with owned_vendor_id) publish, edit, or
 * disable voucher templates that appear on the public /vouchers
 * storefront. Talks to:
 *   GET   /api/admin/vouchers/templates
 *   POST  /api/admin/vouchers/templates
 *   PATCH /api/admin/vouchers/templates/{id}
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { Plus, Ticket, Gift, EyeOff, Eye } from "lucide-react";
import PlatformHeader from "@/components/PlatformHeader";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const authHeader = () => {
  const t = localStorage.getItem("token") || localStorage.getItem("access_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

function fmtMoney(cents = 0, cur = "AUD") {
  return `$${(cents / 100).toFixed(2)} ${cur}`;
}

function TemplateForm({ existing, onSaved, onCancel }) {
  const [form, setForm] = useState(() => ({
    vendor_id:             existing?.vendor_id || "",
    vendor_name:           existing?.vendor_name || "",
    kind:                  existing?.kind || "discounted",
    item_description:      existing?.item_description || "",
    long_description:      existing?.long_description || "",
    purchase_price_cents:  existing?.purchase_price_cents || 4900,
    face_value_cents:      existing?.face_value_cents || 9000,
    currency:              existing?.currency || "AUD",
    image_url:             existing?.image_url || "",
    category:              existing?.category || "food",
    is_gift_ready:         existing?.is_gift_ready ?? true,
    quantity_remaining:    existing?.quantity_remaining || 0,
    active:                existing?.active ?? true,
  }));
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({
    ...f,
    [k]: e.target.type === "checkbox" ? e.target.checked
         : e.target.type === "number"   ? parseInt(e.target.value || "0", 10)
         : e.target.value,
  }));

  const save = async () => {
    setBusy(true);
    try {
      if (existing) {
        const { data } = await axios.patch(
          `${API}/admin/vouchers/templates/${existing.id}`,
          form, { headers: authHeader(), withCredentials: true });
        toast.success("Template updated");
        onSaved(data);
      } else {
        const { data } = await axios.post(
          `${API}/admin/vouchers/templates`,
          form, { headers: authHeader(), withCredentials: true });
        toast.success("Template created");
        onSaved(data);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-stone-200 dark:border-stone-800 p-6 bg-white dark:bg-slate-950 space-y-3"
      data-testid="voucher-template-form">
      <h3 className="font-black text-lg">
        {existing ? `Edit template ${existing.id.slice(-6)}` : "Create voucher template"}
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <input placeholder="Vendor id (slug)"
          disabled={!!existing}
          value={form.vendor_id} onChange={set("vendor_id")}
          data-testid="tpl-vendor-id"
          className="h-11 px-3 rounded-lg border border-stone-300 dark:border-stone-700 bg-transparent"/>
        <input placeholder="Vendor name"
          disabled={!!existing}
          value={form.vendor_name} onChange={set("vendor_name")}
          data-testid="tpl-vendor-name"
          className="h-11 px-3 rounded-lg border border-stone-300 dark:border-stone-700 bg-transparent"/>
        <select value={form.kind} onChange={set("kind")}
          disabled={!!existing}
          data-testid="tpl-kind"
          className="h-11 px-3 rounded-lg border border-stone-300 dark:border-stone-700 bg-transparent">
          <option value="discounted">Discounted deal (12mo credit on expiry)</option>
          <option value="full_price">Full-price / gift (36mo credit on expiry)</option>
        </select>
        <input placeholder="Category (food, spa…)"
          value={form.category} onChange={set("category")}
          data-testid="tpl-category"
          className="h-11 px-3 rounded-lg border border-stone-300 dark:border-stone-700 bg-transparent"/>
      </div>
      <input placeholder="Headline (e.g. '3-course dinner for 2')"
        value={form.item_description} onChange={set("item_description")}
        data-testid="tpl-headline"
        className="w-full h-11 px-3 rounded-lg border border-stone-300 dark:border-stone-700 bg-transparent"/>
      <textarea placeholder="Long description (menu, terms, inclusions)…"
        rows={3}
        value={form.long_description} onChange={set("long_description")}
        data-testid="tpl-long-desc"
        className="w-full px-3 py-2 rounded-lg border border-stone-300 dark:border-stone-700 bg-transparent"/>
      <div className="grid grid-cols-3 gap-3">
        <label className="text-xs">
          Purchase price (cents)
          <input type="number" value={form.purchase_price_cents}
            onChange={set("purchase_price_cents")}
            data-testid="tpl-purchase-price"
            className="mt-1 w-full h-11 px-3 rounded-lg border border-stone-300 dark:border-stone-700 bg-transparent"/>
        </label>
        <label className="text-xs">
          Face value (cents)
          <input type="number" value={form.face_value_cents}
            onChange={set("face_value_cents")}
            data-testid="tpl-face-value"
            className="mt-1 w-full h-11 px-3 rounded-lg border border-stone-300 dark:border-stone-700 bg-transparent"/>
        </label>
        <label className="text-xs">
          Quantity remaining (0 = unlimited)
          <input type="number" value={form.quantity_remaining}
            onChange={set("quantity_remaining")}
            data-testid="tpl-qty"
            className="mt-1 w-full h-11 px-3 rounded-lg border border-stone-300 dark:border-stone-700 bg-transparent"/>
        </label>
      </div>
      <input placeholder="Image URL (optional)"
        value={form.image_url} onChange={set("image_url")}
        data-testid="tpl-image"
        className="w-full h-11 px-3 rounded-lg border border-stone-300 dark:border-stone-700 bg-transparent"/>
      <div className="flex flex-wrap gap-4">
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.is_gift_ready}
            onChange={set("is_gift_ready")}
            data-testid="tpl-is-gift-ready"/>
          Gift-ready (allow "Send as gift" at checkout)
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.active}
            onChange={set("active")}
            data-testid="tpl-active"/>
          Active (visible on public storefront)
        </label>
      </div>
      <div className="flex gap-2 pt-3 border-t border-stone-200 dark:border-stone-800">
        <Button disabled={busy} onClick={save}
          data-testid="tpl-save"
          className="bg-amber-400 text-slate-950 font-black rounded-full h-11 px-6 hover:bg-amber-300">
          {busy ? "Saving…" : existing ? "Save changes" : "Create template"}
        </Button>
        {onCancel && (
          <Button variant="ghost" onClick={onCancel}
            className="rounded-full h-11 px-4">Cancel</Button>
        )}
      </div>
    </div>
  );
}

export default function AdminVoucherTemplatesPage() {
  const { t } = useTranslation();
  const [tpls, setTpls] = useState(null);
  const [creating, setCreating] = useState(false);
  const [editing,  setEditing]  = useState(null);
  const [error,    setError]    = useState("");

  const reload = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/vouchers/templates`,
        { headers: authHeader(), withCredentials: true });
      setTpls(data?.templates || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  };

  useEffect(() => {
    document.title = "Voucher templates · Admin";
    reload();
  }, []);

  const toggle = async (tpl, key) => {
    try {
      await axios.patch(`${API}/admin/vouchers/templates/${tpl.id}`,
        { [key]: !tpl[key] },
        { headers: authHeader(), withCredentials: true });
      toast.success("Updated");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  return (
    <div
      data-testid="admin-voucher-templates-page"
      className="min-h-screen bg-slate-50 text-stone-900 dark:bg-[#0a0908] dark:text-stone-100"
    >
      <PlatformHeader
        brand={{ name: "HustleSuite · Admin", to: "/admin" }}
        showBookCta={false} showCart={false}
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16">
        <div className="flex items-baseline justify-between mb-8">
          <div>
            <p className="text-amber-500 text-[11px] font-bold uppercase tracking-[0.32em]">
              Admin · Vouchers
            </p>
            <h1 className="mt-2 text-3xl md:text-4xl font-black">
              {t("admin_vouchers_title", { defaultValue: "Voucher templates" })}
            </h1>
            <p className="mt-2 text-sm text-stone-500">
              Publish deals to the public /vouchers storefront.
            </p>
          </div>
          {!creating && (
            <Button onClick={() => { setCreating(true); setEditing(null); }}
              data-testid="new-tpl-btn"
              className="bg-amber-400 text-slate-950 font-black rounded-full h-11 px-6 hover:bg-amber-300 inline-flex items-center gap-2">
              <Plus size={16}/> New template
            </Button>
          )}
        </div>

        {error && (
          <div className="rounded-xl border border-red-300 bg-red-50 text-red-800 px-4 py-3 mb-6"
            data-testid="tpl-error">{error}</div>
        )}

        {creating && (
          <div className="mb-6">
            <TemplateForm
              onSaved={() => { setCreating(false); reload(); }}
              onCancel={() => setCreating(false)}
            />
          </div>
        )}

        {editing && (
          <div className="mb-6">
            <TemplateForm existing={editing}
              onSaved={() => { setEditing(null); reload(); }}
              onCancel={() => setEditing(null)}
            />
          </div>
        )}

        <div className="space-y-3">
          {tpls === null && <p className="text-stone-400">Loading…</p>}
          {tpls && tpls.length === 0 && (
            <p className="text-stone-400"
              data-testid="tpl-empty">No templates yet. Create one to start selling deals.</p>
          )}
          {tpls && tpls.map((t) => (
            <div key={t.id}
              data-testid={`tpl-row-${t.id}`}
              className="rounded-xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-4 flex items-center gap-4"
            >
              <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 shrink-0"
                style={t.image_url ? {
                  backgroundImage: `url(${t.image_url})`,
                  backgroundSize: "cover", backgroundPosition: "center" } : undefined}
              />
              <div className="flex-1 min-w-0">
                <p className="text-[10px] uppercase tracking-widest text-stone-500 font-bold truncate">
                  {t.vendor_name} · {t.kind}
                </p>
                <p className="font-black truncate">{t.item_description}</p>
                <p className="text-xs text-stone-500 mt-1">
                  {fmtMoney(t.purchase_price_cents, t.currency)}
                  {t.face_value_cents > t.purchase_price_cents && (
                    <span className="line-through ml-1">
                      {fmtMoney(t.face_value_cents, t.currency)}
                    </span>
                  )}
                  {t.is_gift_ready && (
                    <span className="ml-2 inline-flex items-center gap-1 text-amber-500">
                      <Gift size={10}/> gift-ready
                    </span>
                  )}
                </p>
              </div>
              <button type="button"
                onClick={() => toggle(t, "active")}
                data-testid={`toggle-active-${t.id}`}
                className={`h-8 px-3 rounded-full text-[10px] font-bold uppercase tracking-widest inline-flex items-center gap-1 ${
                  t.active ? "bg-emerald-500/10 text-emerald-500"
                           : "bg-stone-500/10 text-stone-500"
                }`}
              >
                {t.active ? <><Eye size={12}/> Live</>
                          : <><EyeOff size={12}/> Hidden</>}
              </button>
              <Button onClick={() => { setEditing(t); setCreating(false); }}
                data-testid={`edit-${t.id}`}
                variant="ghost"
                className="rounded-full h-9 px-3 text-xs">Edit</Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
